import { useEffect, useRef } from "react";

const STAR_COUNT = 260;

const Starfield = () => {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    let stars = [];
    let shooters = [];
    let w, h, raf;
    let nextShooterAt = 0;

    const seed = () => {
      stars = Array.from({ length: STAR_COUNT }, (_, i) => ({
        x: Math.random(),
        y: Math.random(),
        r: 0.3 + Math.random() * 1.0,
        a: 0.05 + Math.random() * 0.4,
        pink: i % 21 === 0,
        tw: 0.0002 + Math.random() * 0.0006,
        ph: Math.random() * Math.PI * 2,
        depth: 0.04 + Math.random() * 0.18,
      }));
    };

    const resize = () => {
      w = canvas.width = window.innerWidth;
      h = canvas.height = window.innerHeight;
      seed();
    };

    const spawnShooter = () => {
      // Enter from top, right or top-right; travel down-left. Random angle.
      const fromRight = Math.random() > 0.35;
      const startX = fromRight ? w * (0.65 + Math.random() * 0.3) : w * (0.1 + Math.random() * 0.4);
      const startY = -20 - Math.random() * 80;
      const angle = (fromRight ? Math.PI * 0.72 : Math.PI * 0.42) + (Math.random() - 0.5) * 0.15;
      const speed = 8 + Math.random() * 6;
      shooters.push({
        x: startX,
        y: startY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 0,
        maxLife: 90 + Math.floor(Math.random() * 40),
        trail: 22 + Math.floor(Math.random() * 12),
        pink: Math.random() > 0.55,
      });
    };

    const drawShooter = (s) => {
      const tailLen = s.trail;
      const tailX = s.x - s.vx * (tailLen / 8);
      const tailY = s.y - s.vy * (tailLen / 8);
      // Fade in/out
      const t = s.life / s.maxLife;
      const alpha = t < 0.15 ? t / 0.15 : t > 0.85 ? (1 - t) / 0.15 : 1;
      const grad = ctx.createLinearGradient(tailX, tailY, s.x, s.y);
      const rgb = s.pink ? "210, 55, 139" : "255, 255, 255";
      grad.addColorStop(0, `rgba(${rgb}, 0)`);
      grad.addColorStop(1, `rgba(${rgb}, ${0.75 * alpha})`);
      ctx.strokeStyle = grad;
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(tailX, tailY);
      ctx.lineTo(s.x, s.y);
      ctx.stroke();
      // Head glow
      ctx.fillStyle = `rgba(${rgb}, ${0.9 * alpha})`;
      ctx.beginPath();
      ctx.arc(s.x, s.y, 1.4, 0, Math.PI * 2);
      ctx.fill();
    };

    const draw = (t) => {
      ctx.clearRect(0, 0, w, h);
      const scroll = window.scrollY;
      // Stars
      for (const s of stars) {
        let y = (s.y * h - scroll * s.depth) % h;
        if (y < 0) y += h;
        const a = s.a * (0.6 + 0.4 * Math.sin(t * s.tw + s.ph));
        ctx.fillStyle = s.pink
          ? `rgba(210, 55, 139, ${a})`
          : `rgba(255, 255, 255, ${a})`;
        ctx.beginPath();
        ctx.arc(s.x * w, y, s.r, 0, Math.PI * 2);
        ctx.fill();
      }
      // Shooting stars — spawn occasionally
      if (t > nextShooterAt) {
        spawnShooter();
        // Next shooter in 4-14 seconds
        nextShooterAt = t + 4000 + Math.random() * 10000;
      }
      // Update + draw shooters
      shooters = shooters.filter((s) => s.life < s.maxLife && s.x > -100 && s.x < w + 100 && s.y < h + 100);
      for (const s of shooters) {
        s.x += s.vx;
        s.y += s.vy;
        s.life += 1;
        drawShooter(s);
      }
      raf = requestAnimationFrame(draw);
    };

    resize();
    // First shooter arrives 2-5s after mount so the reader notices
    nextShooterAt = performance.now() + 2000 + Math.random() * 3000;
    raf = requestAnimationFrame(draw);
    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={ref}
      data-testid="starfield-canvas"
      className="pointer-events-none fixed inset-0 z-0"
      aria-hidden="true"
    />
  );
};

export default Starfield;
